/**
 * Copyright Highway9 Networks Inc.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Badge, Box, Collapse, IconButton, SxProps, Theme, Tooltip, Typography } from '@h9/ui-lib';
import { useAppDispatch, useAppSelector } from '~/store/hooks';
import { notificationActions, selectLatestNotification, selectUnreadNotificationCount } from '~/store/context/notificationsSlice';
import NotificationPanel from './NotificationPanel';
import { notificationBellIconStyles } from './styles';
import { getNotificationStatusIcon, setNotificationPanelLastOpenedTime } from './utils';
import { Icons } from '../GraphicalResources';

/**
 * NotificationBellIcon Component
 * Displays a bell icon with a badge showing active notification count
 * Shows one-line animated extension to the right when new notifications arrive
 * Opens the notification panel when clicked
 */
export const NotificationBellIcon: React.FC = () => {
  const dispatch = useAppDispatch();
  const activeCount = useAppSelector(selectUnreadNotificationCount);
  const latestNotification = useAppSelector(selectLatestNotification);
  const autoHideTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  // Autoclose extension after 3 seconds
  useEffect(() => {
    if ((latestNotification)) {
      setTimeout(() => {
        dispatch(notificationActions.setLatestNotification(null));
      }, 3500);
    }
  }, [latestNotification, dispatch]);

  const handleExtensionClose = useCallback(() => {
    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
    }
    setTimeout(() => {
      dispatch(notificationActions.setLatestNotification(null));
    }, 300);
  }, [dispatch]);

  return (
    <Tooltip
      title="Tasks"
      placement="bottom"
      key="tasks"
      aria-label="tasks"
      slotProps={
        {
          tooltip: {
            sx: commonStyles.tooltip
          }
        }
      }
    >
    <Box
      component="span"
      sx={notificationBellIconStyles.container}
    >
      <IconButton
        color="inherit"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label={`${activeCount} active notifications`}
        size="small"
        sx={{
          ...notificationBellIconStyles.iconButton,
          ...(Boolean(latestNotification) && notificationBellIconStyles.iconButtonPulsing),
        }}
      >
        <Badge badgeContent={activeCount} color="primary">
          <Icons.scheduleOperation color="primary" />
        </Badge>
      </IconButton>

      {/* One-line Notification Extension - Inline with TopBar */}
      <Collapse
        in={Boolean(latestNotification)}
        orientation="horizontal"
        timeout={300}
        sx={notificationBellIconStyles.collapse}
      >
        <Box
          component="span"
          onClick={handleExtensionClose}
          sx={notificationBellIconStyles.extension}
        >
          {/* Status Icon */}
          {latestNotification && (
            <Box component="span" sx={notificationBellIconStyles.statusIconContainer}>
              {getNotificationStatusIcon(latestNotification.status, 16)}
            </Box>
          )}

          {/* Single Line Content */}
          <Typography
            component="span"
            variant="body2"
            sx={notificationBellIconStyles.title}
          >
            {latestNotification?.title}
          </Typography>

          {/* Progress if in-progress */}
          {latestNotification?.progress !== undefined && latestNotification.status === 'in-progress' && (
            <Typography component="span" variant="caption" color="text.secondary" sx={notificationBellIconStyles.progress}>
              {latestNotification.progress}%
            </Typography>
          )}
        </Box>
      </Collapse>

      {Boolean(anchorEl) && <NotificationPanel
        onClose={() => {
          setAnchorEl(null);
          setNotificationPanelLastOpenedTime();
        }}
        anchorEl={anchorEl}
      />}
    </Box>
    </Tooltip>
  );
};

const commonStyles = {
  tooltip: {
    bgcolor: "#292B2F",
    marginLeft: "8px !important",
    padding: "8px",
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 600,
    color: "#F7F7F7",
  }
} satisfies Record<string, SxProps<Theme>>;

export default NotificationBellIcon;
